import { z } from 'zod';
import { publicConfig } from '../../config/env';
import { normalizePhoneNumber } from '../../lib/phone';
import { authNavigation } from './authNavigation';

const key = 'playerup.provider-session';
const changed = 'playerup-provider-session-changed';
/*
 * Sign-in persists across visits and tabs (PLA-1655, decision 5): the session lives in
 * localStorage, and a change made in another tab is replayed here through the `storage` event.
 * Storage that cannot be read or written counts as signed out rather than failing the page.
 */
function readStored(): string | null {
  try {
    const persisted = localStorage.getItem(key);
    if (persisted !== null) return persisted;
    // A session saved by an earlier, per-tab build moves to persistent storage once.
    const legacy = sessionStorage.getItem(key);
    if (legacy !== null) {
      localStorage.setItem(key, legacy);
      sessionStorage.removeItem(key);
    }
    return legacy;
  } catch {
    return null;
  }
}
function writeStored(value: string | null) {
  try {
    if (value === null) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } else localStorage.setItem(key, value);
  } catch {
    /* Without storage the session lasts only as long as this page's memory. */
  }
}
const sessionSchema = z.object({
  idToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresAt: z.number(),
  sessionId: z.string().min(1).optional(),
  oauth: z.object({ clientId: z.string().min(1), domain: z.string().url() }).optional(),
});
export type Session = z.infer<typeof sessionSchema>;
const unavailableMessage = 'Sign-in is temporarily unavailable. Please try again in a few minutes.';
export class AuthenticationError extends Error {
  constructor(
    message: string,
    readonly kind?: string,
  ) {
    super(message);
  }
}
/** Raised by sign-in for an account whose email was never verified, so the web can verify it. */
export class AccountNotConfirmed extends Error {
  constructor(readonly username: string) {
    super('Verify your email to finish creating your account.');
  }
}
export function signInErrorMessage(error: unknown): string {
  return error instanceof AuthenticationError ||
    error instanceof SignInRequired ||
    error instanceof AccountNotConfirmed
    ? error.message
    : unavailableMessage;
}
/** The Cognito error type behind a failed request, when Cognito reported one. */
export function authenticationErrorKind(error: unknown): string | undefined {
  return error instanceof AuthenticationError ? error.kind : undefined;
}
export async function authenticationFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, { ...init, signal: AbortSignal.timeout(15_000) });
  } catch {
    throw new AuthenticationError(
      'We couldn’t connect to PlayerUp. Check your connection and try again.',
    );
  }
}
export class SignInRequired extends Error {
  constructor() {
    super('Sign in to continue.');
  }
}
export type Challenge = { name: string; session: string; username: string };
let generation = 0;
let refresh: { sessionId: string; promise: Promise<Session> } | undefined;
const httpsOrigin = z
  .string()
  .url()
  .refine((value) => {
    const url = new URL(value);
    return url.protocol === 'https:' && url.origin === value && !url.username && !url.password;
  });
const socialConfigSchema = z.object({
  domain: httpsOrigin,
  clientId: z.string().regex(/^[a-zA-Z0-9_+]+$/),
  providers: z.array(z.enum(['Google', 'SignInWithApple'])),
  callbackUrls: z.array(z.string().url()),
  logoutUrls: z.array(z.string().url()),
  scopes: z.array(z.string().min(1)),
  issuer: z.string().url(),
});
export type SocialConfig = z.infer<typeof socialConfigSchema>;
const configSchema = z.object({
  region: z.string().regex(/^[a-z]{2}-[a-z]+-\d$/),
  clientId: z.string().regex(/^[a-zA-Z0-9_+]+$/),
  // Invalid social configuration must not disable existing password sign-in.
  socialAuth: socialConfigSchema.nullish().catch(null),
});
let config: Promise<z.infer<typeof configSchema>> | undefined;
export const authenticationGeneration = () => generation;
export function loadProviderSession(): Session | null {
  try {
    const value = sessionSchema.parse(JSON.parse(readStored() ?? 'null'));
    if (!value.sessionId) {
      value.sessionId = crypto.randomUUID();
      writeStored(JSON.stringify(value));
    }
    return value;
  } catch {
    return null;
  }
}
export function saveProviderSession(value: Session | null) {
  generation++;
  writeStored(
    value ? JSON.stringify({ ...value, sessionId: value.sessionId ?? crypto.randomUUID() }) : null,
  );
  window.dispatchEvent(new Event(changed));
}
export function watchProviderSession(callback: () => void) {
  // Another tab signed in, refreshed or signed out: drop any refresh started against the old
  // session and let this tab re-read the stored one.
  const fromOtherTab = (event: StorageEvent) => {
    if (event.key !== key && event.key !== null) return;
    generation++;
    callback();
  };
  window.addEventListener(changed, callback);
  window.addEventListener('storage', fromOtherTab);
  return () => {
    window.removeEventListener(changed, callback);
    window.removeEventListener('storage', fromOtherTab);
  };
}
// Provider availability is a runtime decision. Callers initiating social sign-in
// must bypass the lifetime cache used by existing password/session operations.
export async function freshAuthenticationConfig() {
  try {
    const response = await authenticationFetch(
      `${publicConfig.apiBaseUrl}/session-provider/config`,
      {
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      },
    );
    if (!response.ok) throw new AuthenticationError(unavailableMessage);
    return configSchema.parse(await response.json());
  } catch {
    throw new AuthenticationError(unavailableMessage);
  }
}
export async function authenticationConfig() {
  config ??= freshAuthenticationConfig().catch((error: unknown) => {
    config = undefined;
    throw error;
  });
  return config;
}
const resultSchema = z.object({
  AuthenticationResult: z
    .object({
      IdToken: z.string(),
      RefreshToken: z.string().optional(),
      ExpiresIn: z.number().positive(),
    })
    .optional(),
  ChallengeName: z.string().optional(),
  Session: z.string().optional(),
  ChallengeParameters: z.record(z.string(), z.string()).optional(),
});
async function cognito(operation: string, input: Record<string, unknown>) {
  const settings = await authenticationConfig();
  const response = await authenticationFetch(
    `https://cognito-idp.${settings.region}.amazonaws.com/`,
    {
      method: 'POST',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
      },
      body: JSON.stringify({ ClientId: settings.clientId, ...input }),
    },
  );
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const parsed = z.object({ __type: z.string().optional() }).safeParse(body);
    const kind = parsed.success ? parsed.data.__type?.split('#').at(-1) : undefined;
    const messages: Record<string, string> = {
      NotAuthorizedException:
        operation === 'RespondToAuthChallenge'
          ? 'Your sign-in attempt has expired. Choose Start again to sign in.'
          : 'Your email, phone number, or password is incorrect. Please check your details and try again.',
      UserNotFoundException:
        'Your email, phone number, or password is incorrect. Please check your details and try again.',
      TooManyRequestsException:
        'Too many sign-in attempts. Please wait a few minutes and try again.',
      UserNotConfirmedException:
        'Please verify your account in the PlayerUp app, then sign in here.',
      PasswordResetRequiredException:
        'Please reset your password in the PlayerUp app, then sign in here.',
      CodeMismatchException:
        'That verification code is incorrect. Please check the code and try again.',
      ExpiredCodeException:
        'That verification code has expired. Choose Start again to request a new code.',
      ...accountMessages(operation),
    };
    throw new AuthenticationError(
      response.status >= 500 ? unavailableMessage : (messages[kind ?? ''] ?? unavailableMessage),
      kind,
    );
  }
  try {
    return resultSchema.parse(await response.json());
  } catch {
    throw new AuthenticationError(unavailableMessage);
  }
}
function completed(result: z.infer<typeof resultSchema>, previousRefresh?: string): Session {
  const value = result.AuthenticationResult;
  if (!value || !(value.RefreshToken ?? previousRefresh)) throw new SignInRequired();
  return {
    idToken: value.IdToken,
    refreshToken: value.RefreshToken ?? previousRefresh!,
    expiresAt: Date.now() + value.ExpiresIn * 1000,
  };
}
function normalizeSignInIdentifier(value: string): string {
  const trimmed = value.trim();
  if (z.email().safeParse(trimmed).success) return trimmed;
  const phone = /^[+\d\s().-]+$/.test(trimmed) ? normalizePhoneNumber(trimmed) : null;
  if (!phone) throw new AuthenticationError('Enter a valid email address or phone number.');
  return phone;
}
/**
 * Messages for the athlete account operations (PLA-1655). Sign-in keeps its own wording above; these
 * apply to sign-up, verification and recovery, where the visitor can fix the input on the web.
 */
function accountMessages(operation: string): Record<string, string> {
  switch (operation) {
    case 'SignUp':
      return {
        UsernameExistsException:
          'An account with this email already exists. Sign in instead, or reset your password.',
        InvalidPasswordException:
          'Choose a stronger password: at least 8 characters with an uppercase letter, a lowercase letter and a number.',
        InvalidParameterException: 'Check your email address and password, then try again.',
      };
    case 'ConfirmSignUp':
      return {
        CodeMismatchException: 'Incorrect code. Please try again.',
        ExpiredCodeException: 'That code has expired. Request a new code and try again.',
        NotAuthorizedException: 'This account is already verified. Sign in to continue.',
        LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
        TooManyFailedAttemptsException:
          'Too many attempts. Please wait a few minutes and try again.',
      };
    case 'ResendConfirmationCode':
    case 'ForgotPassword':
      return {
        LimitExceededException: 'Please wait a few minutes before requesting another code.',
        UserNotFoundException:
          operation === 'ForgotPassword'
            ? 'If an account exists for this email, we sent it a reset code.'
            : 'We couldn’t find an account for this email.',
        InvalidParameterException:
          'Password reset is unavailable for this account. If you signed up with TeamSnap, sign in with TeamSnap in the PlayerUp app.',
        CodeDeliveryFailureException: 'We couldn’t send a code right now. Please try again.',
      };
    case 'ConfirmForgotPassword':
      return {
        CodeMismatchException: 'Incorrect code. Please try again.',
        ExpiredCodeException: 'That code has expired. Request a new code and try again.',
        InvalidPasswordException:
          'Choose a stronger password: at least 8 characters with an uppercase letter, a lowercase letter and a number.',
        LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
      };
    default:
      return {};
  }
}
export async function signIn(
  username: string,
  password: string,
  challenge?: Challenge,
  code?: string,
): Promise<Challenge | null> {
  try {
    return await signInOnce(username, password, challenge, code);
  } catch (error) {
    // With the athlete web on, an unverified account is finished here rather than in the app.
    if (
      publicConfig.athleteWebEnabled &&
      error instanceof AuthenticationError &&
      error.kind === 'UserNotConfirmedException'
    ) {
      throw new AccountNotConfirmed(username.trim());
    }
    throw error;
  }
}
async function signInOnce(
  username: string,
  password: string,
  challenge?: Challenge,
  code?: string,
): Promise<Challenge | null> {
  const epoch = generation;
  const identifier = challenge?.username ?? normalizeSignInIdentifier(username);
  const codeKeys: Record<string, string> = {
    SMS_MFA: 'SMS_MFA_CODE',
    SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE',
    EMAIL_OTP: 'EMAIL_OTP_CODE',
    EMAIL_MFA: 'EMAIL_MFA_CODE',
  };
  const result = challenge
    ? await cognito('RespondToAuthChallenge', {
        ChallengeName: challenge.name,
        Session: challenge.session,
        ChallengeResponses: { USERNAME: challenge.username, [codeKeys[challenge.name]]: code },
      })
    : await cognito('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: { USERNAME: identifier, PASSWORD: password },
      });
  if (epoch !== generation) throw new SignInRequired();
  if (result.AuthenticationResult) {
    saveProviderSession(completed(result));
    return null;
  }
  if (!result.ChallengeName || !codeKeys[result.ChallengeName] || !result.Session)
    throw new AuthenticationError(
      'Complete your account setup or password update in the PlayerUp app, then sign in here again.',
    );
  return {
    name: result.ChallengeName,
    session: result.Session,
    username:
      result.ChallengeParameters?.USER_ID_FOR_SRP ??
      result.ChallengeParameters?.USERNAME ??
      identifier,
  };
}
export async function providerToken(forceRefresh = false) {
  const saved = loadProviderSession();
  if (!saved) throw new SignInRequired();
  if (!forceRefresh && saved.expiresAt > Date.now() + 30_000) return saved.idToken;
  const epoch = generation;
  if (!refresh || refresh.sessionId !== saved.sessionId) {
    const operation = (
      saved.oauth
        ? oauthTokens(
            saved.oauth,
            { grant_type: 'refresh_token', refresh_token: saved.refreshToken },
            saved.refreshToken,
          )
        : cognito('InitiateAuth', {
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            AuthParameters: { REFRESH_TOKEN: saved.refreshToken },
          }).then((result) => completed(result, saved.refreshToken))
    )
      .then((result) => {
        if (epoch !== generation) throw new SignInRequired();
        const next = { ...result, sessionId: saved.sessionId, oauth: saved.oauth };
        saveProviderSession(next);
        return next;
      })
      .catch(() => {
        if (epoch === generation) saveProviderSession(null);
        throw new SignInRequired();
      })
      .finally(() => {
        if (refresh?.promise === operation) refresh = undefined;
      });
    refresh = { sessionId: saved.sessionId!, promise: operation };
  }
  return (await refresh.promise).idToken;
}
export async function oauthTokens(
  settings: Pick<SocialConfig, 'domain' | 'clientId'>,
  input: Record<string, string>,
  previousRefresh?: string,
): Promise<Session> {
  const response = await authenticationFetch(`${httpsOrigin.parse(settings.domain)}/oauth2/token`, {
    method: 'POST',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: settings.clientId, ...input }).toString(),
  });
  if (!response.ok)
    throw new AuthenticationError('Your sign-in could not be completed. Please start again.');
  const result = z
    .object({
      id_token: z.string().min(1),
      refresh_token: z.string().min(1).optional(),
      expires_in: z.number().positive(),
      token_type: z.literal('Bearer'),
    })
    .parse(await response.json());
  const refreshToken = result.refresh_token ?? previousRefresh;
  if (!refreshToken) throw new AuthenticationError(unavailableMessage);
  return {
    idToken: result.id_token,
    refreshToken,
    expiresAt: Date.now() + result.expires_in * 1000,
    oauth: { clientId: settings.clientId, domain: settings.domain },
  };
}
/**
 * Sign this web session out. A password session ends here; a Google or Apple session also leaves
 * for Cognito's managed logout, which can only return to a registered logout URL (`/login`).
 *
 * `returnTo` is the page of this app to come back to instead, for a "Not you?" on a public booking
 * page (PLA-1866). It is used only when it is registered. Booking pages carry a trainer ID or a
 * link token, so none of them can be, and the session is then revoked and cleared locally while the
 * page stays where it is: the next sign-in may be offered the same Google or Apple account by the
 * provider, and the visitor can choose another there. Leaving for `/login` would lose the booking.
 */
export async function signOut(options: { returnTo?: string } = {}) {
  const saved = loadProviderSession();
  saveProviderSession(null);
  sessionStorage.removeItem('playerup.oauth-transaction');
  // The social round trip's return path: consumed by `/account` after sign-in, so a sign-out ends
  // whatever was left of it, whichever page asked.
  sessionStorage.removeItem('playerup.account-return');
  const epoch = generation;
  // Revoke this web session only; no global sign-out of the mobile app.
  if (saved) {
    try {
      await cognito('RevokeToken', {
        Token: saved.refreshToken,
        ...(saved.oauth ? { ClientId: saved.oauth.clientId } : {}),
      });
    } catch {
      /* Local access is already cleared. */
    }
  }
  if (saved?.oauth && epoch === generation) {
    try {
      const settings = (await authenticationConfig()).socialAuth;
      const destination = `${window.location.origin}${options.returnTo ?? '/login'}`;
      if (
        settings?.clientId === saved.oauth.clientId &&
        settings.domain === saved.oauth.domain &&
        settings.logoutUrls.includes(destination)
      ) {
        authNavigation.assign(
          `${settings.domain}/logout?${new URLSearchParams({ client_id: settings.clientId, logout_uri: destination })}`,
        );
      }
    } catch {
      /* Local sign-out remains complete when configuration is unavailable. */
    }
  }
}

/** Roles a person may choose at web sign-up (PLA-1709): iOS's Athlete and "Coach or Team Admin". */
export type SignUpRole = 'player' | 'coach';

/*
 * Account operations (PLA-1655). They call Cognito directly with the same public client as sign-in
 * and the iOS app, and create accounts exactly as iOS does: the chosen role is set once, in the
 * sign-up request (PLA-1709). Cognito's post-confirmation trigger creates the PlayerUp user with it;
 * the web never writes the role itself.
 *
 * A coach also gives their name here, which the same trigger saves. Coach setup cannot start without
 * it: the All Athletes training group it configures needs the coach's name (PLA-1709).
 */
export async function signUp(
  email: string,
  password: string,
  role: SignUpRole,
  name?: { firstName: string; lastName: string },
) {
  await cognito('SignUp', {
    Username: email.trim(),
    Password: password,
    UserAttributes: [
      { Name: 'email', Value: email.trim() },
      { Name: 'custom:userType', Value: role },
      { Name: 'custom:signInProvider', Value: 'playerup' },
      ...(name
        ? [
            { Name: 'given_name', Value: name.firstName.trim() },
            { Name: 'family_name', Value: name.lastName.trim() },
          ]
        : []),
    ],
  });
}
export async function confirmSignUp(email: string, code: string) {
  await cognito('ConfirmSignUp', { Username: email.trim(), ConfirmationCode: code.trim() });
}
export async function resendSignUpCode(email: string) {
  await cognito('ResendConfirmationCode', { Username: email.trim() });
}
export async function startPasswordReset(email: string) {
  try {
    await cognito('ForgotPassword', { Username: email.trim() });
  } catch (error) {
    // Answer the same way whether or not the account exists, so the form cannot be used to test
    // which emails have PlayerUp accounts. A wrong email simply never receives a code.
    if (authenticationErrorKind(error) === 'UserNotFoundException') return;
    throw error;
  }
}
export async function finishPasswordReset(email: string, code: string, password: string) {
  await cognito('ConfirmForgotPassword', {
    Username: email.trim(),
    ConfirmationCode: code.trim(),
    Password: password,
  });
}
